/**
 * WEBSOCKET / SIGNALR ADMIN SERVICE: MEMORAMA (MEMORY 1 VS 1)
 * Wrapper de comunicación en tiempo real para el Panel del Operador / Administrador del Estadio.
 * Gestiona la conexión persistente con SignalR Hub (`/hub/entertainment`) para recibir telemetría de 100ms,
 * cambios de fase (cover, game_ready, countdown, preview, playing, finished), volteo de cartas, aciertos/fallos,
 * sincronización de tableros con blindaje Zero-Knowledge y controles de emergencia.
 * 
 * @module utilities/WS/Admin/MemoryWS
 */

import SignalRService from '../SignalRService';

/**
 * Referencia al handler de escucha registrado en SignalRService para mensajes generales.
 * @type {Function|null}
 */
let registeredMessageHandler = null;

/**
 * Referencia al handler de reconexión exitosa registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectedHandler = null;

/**
 * Referencia al handler de reconexión en progreso registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectingHandler = null;

/**
 * Referencia al handler de cierre definitivo de conexión.
 * @type {Function|null}
 */
let registeredCloseHandler = null;

/**
 * Conecta el panel de administración al Hub de SignalR con el rol de operador de Memorama ('memory:admin').
 * Se suscribe a la sala correspondiente (`roomId`) y despacha eventos a los callbacks registrados.
 * 
 * Eventos procesados:
 * - `memory_sync_state` / `memory_state` / `memory_cover_shown` / `memory_game_ready` / `memory_game_shown`: Estado general de sala.
 * - `memory_telemetry`: Telemetría rápida a 100ms (cronómetro restante, puntajes, flips y estados de jugadores).
 * - `memory_countdown`: Conteo regresivo oficial 3..2..1.
 * - `memory_preview`: Fase de memorización con descubrimiento temporal de cartas.
 * - `memory_preview_ended`: Finalización de la fase de memorización y desbloqueo de tableros.
 * - `memory_playing_started` / `memory_start`: Transición a la fase activa de juego libre.
 * - `memory_card_flipped`: Revelación autoritativa de una carta volteada legalmente.
 * - `memory_match_success` / `match_success`: Coincidencia y acierto de un par de cartas.
 * - `memory_match_failed` / `match_failed`: Fallo de coincidencia entre dos cartas.
 * - `memory_cards_unflipped` / `cards_unflipped`: Notificación de cartas ocultadas boca abajo tras retardo.
 * - `memory_all_pairs_matched` / `all_pairs_matched`: Victoria instantánea al completar todos los pares configurados.
 * - `memory_finished` / `memory_game_over` / `memory_game_finished`: Fin oficial de la partida.
 * - `memory_names_updated` / `memory_update_names`: Actualización en caliente de nombres de participantes.
 * - `memory_score_adjusted`: Modificación manual de puntaje (+1/-1 pt).
 * - `memory_match_reset`: Reinicio de partida al estado 'game_ready'.
 * - `memory_player_joined`: Conexión / ingreso de participante a uno de los dos slots.
 * - `memory_player_left` / `leave_game`: Abandono voluntario de un jugador.
 * - `memory_player_disconnected`: Desconexión de socket de un jugador.
 * 
 * @param {Function} [onIncomingMessage=() => {}] - Callback general para procesar cualquier mensaje emitido por el Hub.
 * @param {Function} [onStatusChange=() => {}] - Callback para notificar cambios de estado de conexión ('connecting', 'connected', 'reconnecting', 'error', 'closed').
 * @param {string|number} [roomId=''] - Identificador GUID o numérico de la sala para filtrar el tráfico.
 * @param {Object} [eventCallbacks={}] - Callbacks específicos por tipo de evento.
 * @param {Function} [eventCallbacks.onSyncState=null] - Callback para sincronización completa del estado de la sala.
 * @param {Function} [eventCallbacks.onTelemetry=null] - Callback para telemetría continua a 100ms.
 * @param {Function} [eventCallbacks.onCountdown=null] - Callback para conteo regresivo 3..2..1.
 * @param {Function} [eventCallbacks.onPreview=null] - Callback para inicio de fase de memorización previa.
 * @param {Function} [eventCallbacks.onPreviewEnded=null] - Callback para fin de preview.
 * @param {Function} [eventCallbacks.onPlayingStarted=null] - Callback para inicio de fase activa 'playing'.
 * @param {Function} [eventCallbacks.onCardFlipped=null] - Callback para carta volteada.
 * @param {Function} [eventCallbacks.onMatchSuccess=null] - Callback para acierto de par (match).
 * @param {Function} [eventCallbacks.onMatchFailed=null] - Callback para fallo de par (no match).
 * @param {Function} [eventCallbacks.onCardsUnflipped=null] - Callback para cartas devueltas boca abajo.
 * @param {Function} [eventCallbacks.onAllPairsMatched=null] - Callback para todos los pares completados.
 * @param {Function} [eventCallbacks.onFinished=null] - Callback para fin de partida.
 * @param {Function} [eventCallbacks.onNamesUpdated=null] - Callback para nombres modificados.
 * @param {Function} [eventCallbacks.onScoreAdjusted=null] - Callback para puntaje ajustado.
 * @param {Function} [eventCallbacks.onMatchReset=null] - Callback para partida reiniciada.
 * @param {Function} [eventCallbacks.onPlayerJoined=null] - Callback para jugador conectado a un slot.
 * @param {Function} [eventCallbacks.onPlayerLeft=null] - Callback para jugador que abandonó.
 * @param {Function} [eventCallbacks.onPlayerDisconnected=null] - Callback para jugador desconectado.
 */
export const ConnectWebSocketForMemoryAdmin = (
    onIncomingMessage = () => {},
    onStatusChange = () => {},
    roomId = '',
    eventCallbacks = {}
) => {
    onStatusChange('connecting');

    // Desuscribir handlers previos si existieran para evitar fugas de memoria o ejecuciones duplicadas
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
    }

    // Handler de mensajes entrantes con filtrado estricto por sala / roomId
    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        // Extraer roomId o screenId del mensaje envelope
        const envelopeRoomId = messageEnvelope.roomId
            || messageEnvelope.RoomId
            || messageEnvelope.screenId
            || messageEnvelope.ScreenId
            || messageEnvelope.payload?.roomId
            || messageEnvelope.payload?.RoomId
            || messageEnvelope.payload?.screenId
            || messageEnvelope.payload?.ScreenId
            || messageEnvelope.data?.roomId
            || messageEnvelope.data?.RoomId
            || messageEnvelope.data?.screenId;

        // Si se especificó roomId y el mensaje contiene identificador, validar aislamiento multi-tenant
        if (roomId && envelopeRoomId !== undefined && envelopeRoomId !== null) {
            if (String(envelopeRoomId).toLowerCase() !== String(roomId).toLowerCase()) {
                return;
            }
        }

        const rawType = String(messageEnvelope.type || messageEnvelope.Type || '').toLowerCase();
        const payloadData = messageEnvelope.payload !== undefined ? messageEnvelope.payload : messageEnvelope.data;

        // Despacho a callbacks específicos según tipo de evento
        switch (rawType) {
            // Sincronización general de estado
            case 'memory_sync_state':
            case 'memory_state':
            case 'memory_cover_shown':
            case 'memory_cover':
            case 'memory_game_ready':
            case 'memory_game_shown':
                if (typeof eventCallbacks.onSyncState === 'function') {
                    try {
                        eventCallbacks.onSyncState(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onSyncState:', err);
                    }
                }
                break;

            // Telemetría continua a 100ms
            case 'memory_telemetry':
                if (typeof eventCallbacks.onTelemetry === 'function') {
                    try {
                        eventCallbacks.onTelemetry(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onTelemetry:', err);
                    }
                }
                break;

            // Conteo regresivo
            case 'memory_countdown':
                if (typeof eventCallbacks.onCountdown === 'function') {
                    try {
                        eventCallbacks.onCountdown(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onCountdown:', err);
                    }
                }
                break;

            // Fase de memorización (Preview)
            case 'memory_preview':
            case 'memory_preview_start':
                if (typeof eventCallbacks.onPreview === 'function') {
                    try {
                        eventCallbacks.onPreview(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onPreview:', err);
                    }
                }
                break;

            case 'memory_preview_ended':
                if (typeof eventCallbacks.onPreviewEnded === 'function') {
                    try {
                        eventCallbacks.onPreviewEnded(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onPreviewEnded:', err);
                    }
                }
                break;

            // Inicio de partida activa
            case 'memory_playing_started':
            case 'memory_start':
            case 'memory_game_start':
                if (typeof eventCallbacks.onPlayingStarted === 'function') {
                    try {
                        eventCallbacks.onPlayingStarted(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onPlayingStarted:', err);
                    }
                }
                break;

            // Volteo de carta
            case 'memory_card_flipped':
                if (typeof eventCallbacks.onCardFlipped === 'function') {
                    try {
                        eventCallbacks.onCardFlipped(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onCardFlipped:', err);
                    }
                }
                break;

            // Acierto de par
            case 'memory_match_success':
            case 'match_success':
                if (typeof eventCallbacks.onMatchSuccess === 'function') {
                    try {
                        eventCallbacks.onMatchSuccess(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onMatchSuccess:', err);
                    }
                }
                break;

            // Fallo de par
            case 'memory_match_failed':
            case 'match_failed':
                if (typeof eventCallbacks.onMatchFailed === 'function') {
                    try {
                        eventCallbacks.onMatchFailed(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onMatchFailed:', err);
                    }
                }
                break;

            // Cartas vueltas a tapar
            case 'memory_cards_unflipped':
            case 'cards_unflipped':
                if (typeof eventCallbacks.onCardsUnflipped === 'function') {
                    try {
                        eventCallbacks.onCardsUnflipped(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onCardsUnflipped:', err);
                    }
                }
                break;

            // Todos los pares completados
            case 'memory_all_pairs_matched':
            case 'all_pairs_matched':
                if (typeof eventCallbacks.onAllPairsMatched === 'function') {
                    try {
                        eventCallbacks.onAllPairsMatched(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onAllPairsMatched:', err);
                    }
                }
                break;

            // Fin de partida
            case 'memory_finished':
            case 'memory_game_over':
            case 'memory_game_finished':
                if (typeof eventCallbacks.onFinished === 'function') {
                    try {
                        eventCallbacks.onFinished(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onFinished:', err);
                    }
                }
                break;

            // Nombres actualizados
            case 'memory_names_updated':
            case 'memory_update_names':
                if (typeof eventCallbacks.onNamesUpdated === 'function') {
                    try {
                        eventCallbacks.onNamesUpdated(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onNamesUpdated:', err);
                    }
                }
                break;

            // Puntaje ajustado
            case 'memory_score_adjusted':
                if (typeof eventCallbacks.onScoreAdjusted === 'function') {
                    try {
                        eventCallbacks.onScoreAdjusted(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onScoreAdjusted:', err);
                    }
                }
                break;

            // Partida reiniciada
            case 'memory_match_reset':
                if (typeof eventCallbacks.onMatchReset === 'function') {
                    try {
                        eventCallbacks.onMatchReset(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onMatchReset:', err);
                    }
                }
                break;

            // Ingreso de participante
            case 'memory_player_joined':
                if (typeof eventCallbacks.onPlayerJoined === 'function') {
                    try {
                        eventCallbacks.onPlayerJoined(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onPlayerJoined:', err);
                    }
                }
                break;

            // Abandono voluntario
            case 'memory_player_left':
            case 'leave_game':
                if (typeof eventCallbacks.onPlayerLeft === 'function') {
                    try {
                        eventCallbacks.onPlayerLeft(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onPlayerLeft:', err);
                    }
                }
                break;

            // Desconexión de socket
            case 'memory_player_disconnected':
                if (typeof eventCallbacks.onPlayerDisconnected === 'function') {
                    try {
                        eventCallbacks.onPlayerDisconnected(payloadData, messageEnvelope);
                    } catch (err) {
                        console.error('[MemoryAdminWS] Error en callback onPlayerDisconnected:', err);
                    }
                }
                break;

            default:
                break;
        }

        // Despachar también al handler general
        if (typeof onIncomingMessage === 'function') {
            try {
                onIncomingMessage(messageEnvelope);
            } catch (err) {
                console.error('[MemoryAdminWS] Error en onIncomingMessage general:', err);
            }
        }
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectingHandler = () => {
        onStatusChange('reconnecting');
    };

    registeredCloseHandler = () => {
        onStatusChange('closed');
    };

    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);

    SignalRService.connect({
        tipo: 'memory:admin',
        roomId: roomId ? String(roomId) : '',
        screenId: roomId ? String(roomId) : ''
    })
        .then(() => {
            onStatusChange('connected');
        })
        .catch((connectionError) => {
            console.error('[MemoryAdminWS] Error al conectar panel de operador con SignalR Hub:', connectionError);
            onStatusChange('error');
        });
};

/**
 * Desconecta limpiamente la sesión SignalR del operador de Memorama y remueve los escuchas registrados.
 */
export const DisconnectWebSocketForMemoryAdmin = () => {
    try {
        if (registeredMessageHandler) {
            SignalRService.off('message', registeredMessageHandler);
            registeredMessageHandler = null;
        }
        if (registeredReconnectedHandler) {
            SignalRService.off('reconnected', registeredReconnectedHandler);
            registeredReconnectedHandler = null;
        }
        if (registeredReconnectingHandler) {
            SignalRService.off('reconnecting', registeredReconnectingHandler);
            registeredReconnectingHandler = null;
        }
        if (registeredCloseHandler) {
            SignalRService.off('close', registeredCloseHandler);
            registeredCloseHandler = null;
        }
        SignalRService.disconnect();
    } catch (disconnectionError) {
        console.error('[MemoryAdminWS] Error al desconectar la sesión SignalR del operador:', disconnectionError);
    }
};

export default {
    ConnectWebSocketForMemoryAdmin,
    DisconnectWebSocketForMemoryAdmin
};
